import {
  Component,
  HostListener,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize, map, take } from 'rxjs';
import { AdesaoStatus } from '../../core/enum/adesao-status.enum';
import { Adesao, AdesaoFiltros } from '../../core/models/adesao.model';
import { Campanha } from '../../core/models/campanha.model';
import { CampanhaService } from '../../core/services/campanha.service';
import { podeEditarCampanha } from '../../core/utils/campanha-permissions.util';
import { ToastService } from '../../core/services/toast.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { toLocalIsoDate } from '../../core/validators/campanha-vigencia.validator';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { IndicadorCardComponent } from '../../shared/components/indicador-card/indicador-card.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import {
  StatusPieChartComponent,
  StatusPieChartSegment,
} from '../../shared/components/status-pie-chart/status-pie-chart.component';
import { ProtocoloLogDialogComponent } from '../../shared/components/protocolo-log-dialog/protocolo-log-dialog.component';
import { AdesaoFilterBarComponent } from './adesao-filter-bar.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import {
  ActionsMenuPosition,
  adjustActionsMenuPosition,
  getActionsMenuPosition,
} from '../../shared/utils/actions-menu.util';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-campanha-detalhe',
  imports: [
    RouterLink,
    BackButtonComponent,
    IndicadorCardComponent,
    AdesaoFilterBarComponent,
    DataTableComponent,
    StatusBadgeComponent,
    StatusPieChartComponent,
    ProtocoloLogDialogComponent,
    PaginationComponent,
    EmptyStateComponent,
  ],
  templateUrl: './campanha-detalhe.component.html',
  styleUrl: './campanha-detalhe.component.scss',
})
export class CampanhaDetalheComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly campanhaService = inject(CampanhaService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly injector = inject(Injector);

  private readonly campanhaId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id') ?? '')),
    { initialValue: '' },
  );

  private readonly adesoesSignal = signal<readonly Adesao[]>([]);
  private readonly adesaoFiltros = signal<AdesaoFiltros>({});

  readonly campanha = signal<Campanha | undefined>(undefined);
  readonly adesoes = this.adesoesSignal.asReadonly();
  readonly currentPage = signal(1);
  readonly exportando = signal(false);
  readonly pageSize = PAGE_SIZE;
  readonly formatDate = formatDateIsoToBr;
  readonly menuAbertoId = signal<string | null>(null);
  readonly menuPosition = signal<ActionsMenuPosition | null>(null);
  readonly protocoloSelecionado = signal<Adesao | undefined>(undefined);

  readonly adesoesPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.adesoesSignal().slice(inicio, inicio + PAGE_SIZE);
  });

  readonly adesoesPendentes = computed(
    () => this.adesoesSignal().filter((a) => a.status === AdesaoStatus.Pendente).length,
  );

  readonly adesoesConcluidas = computed(
    () => this.adesoesSignal().filter((a) => a.status === AdesaoStatus.Concluido).length,
  );

  readonly adesoesSolicitadas = computed(
    () => this.adesoesSignal().filter((a) => a.status === AdesaoStatus.Solicitado).length,
  );

  readonly adesoesCanceladas = computed(
    () => this.adesoesSignal().filter((a) => a.status === AdesaoStatus.Cancelada).length,
  );

  // Todo status de adesão precisa ter um segmento: sem isso o total do gráfico diverge da lista.
  readonly adesoesPorStatus = computed<readonly StatusPieChartSegment[]>(() => [
    { label: 'Pendente', value: this.adesoesPendentes(), color: 'var(--color-yellow-v500)' },
    { label: 'Solicitado', value: this.adesoesSolicitadas(), color: 'var(--color-primary-v500)' },
    { label: 'Concluído', value: this.adesoesConcluidas(), color: 'var(--color-green-v500)' },
    { label: 'Cancelado', value: this.adesoesCanceladas(), color: 'var(--color-red-v500)' },
  ]);

  readonly adesoesTotalLabel = computed(() => {
    const totalFromCampanha = this.campanha()?.adesoes;
    if (totalFromCampanha != null) {
      return totalFromCampanha.toLocaleString('pt-BR');
    }

    return this.adesoesSignal().length.toLocaleString('pt-BR');
  });

  readonly conversaoLabel = computed(() => {
    const percent = this.campanha()?.conversaoPercent;
    return percent != null ? `${percent}%` : '—';
  });

  readonly periodoLabel = computed(() => {
    const campanhaAtual = this.campanha();
    if (!campanhaAtual) {
      return '';
    }

    const inicio = this.formatPeriodoPonta(campanhaAtual.dataInicio, campanhaAtual.horaInicio);
    const fim = this.formatPeriodoPonta(campanhaAtual.dataFim, campanhaAtual.horaFim);
    return `${inicio} a ${fim}`;
  });

  private formatPeriodoPonta(data: string, hora?: string): string {
    const dataFormatada = this.formatDate(data);
    return hora ? `${dataFormatada} ${hora}` : dataFormatada;
  }

  /** Espelha CampanhaVigenciaGuard.GarantirNaoVigenteEAtiva: só Rascunho ou Agendada pode ser editada. */
  readonly podeEditar = computed(() => {
    const campanhaAtual = this.campanha();
    return !!campanhaAtual && podeEditarCampanha(campanhaAtual.status);
  });

  readonly podeVisualizar = computed(
    () => {
      const campanhaAtual = this.campanha();
      return !!campanhaAtual && !podeEditarCampanha(campanhaAtual.status);
    },
  );

  ngOnInit(): void {
    this.loadCampanha();
  }

  onAdesaoFiltersChange(filtros: AdesaoFiltros): void {
    this.adesaoFiltros.set(filtros);
    this.currentPage.set(1);
    this.carregarAdesoes();
  }

  /**
   * Recarrega as adesões respeitando o filtro ativo. A carga inicial vem do
   * `filtersChange` que a `app-adesao-filter-bar` emite no próprio ngOnInit —
   * aqui só para refresh após mutação (ex.: importação de participantes), já que
   * a filter bar não é recriada quando `campanha()` continua preenchida.
   */
  private carregarAdesoes(): void {
    const id = this.campanhaId();
    if (!id) {
      return;
    }

    this.campanhaService.listarAdesoes(id, this.adesaoFiltros()).subscribe((lista) => {
      this.adesoesSignal.set(lista);
    });
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  onEditarCampanha(): void {
    const id = this.campanhaId();
    if (!id) {
      return;
    }

    if (!this.podeEditar()) {
      this.onVisualizarCampanha();
      return;
    }

    void this.router.navigate(['/campanhas', id, 'editar']);
  }

  onVisualizarCampanha(): void {
    const id = this.campanhaId();
    if (!id || !this.podeVisualizar()) {
      return;
    }

    void this.router.navigate(['/campanhas', id, 'editar'], {
      queryParams: { modo: 'visualizar' },
    });
  }

  // Removido por solicitação do PO — botão "Exportar" retirado da tela de detalhe da campanha.
  /** Baixa a planilha das adesões no mesmo recorte da tela (nome e status filtrados). */
  onExportar(): void {
    const id = this.campanhaId();
    if (!id || this.exportando()) {
      return;
    }

    this.exportando.set(true);

    this.campanhaService
      .exportarAdesoes(id, this.adesaoFiltros())
      .pipe(
        take(1),
        finalize(() => this.exportando.set(false)),
      )
      .subscribe({
        next: (arquivo) => this.baixarArquivo(arquivo, this.nomeDoArquivoExportado()),
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível exportar as adesões.';
          this.toastService.show(message, 'error');
        },
      });
  }

  /** Id da campanha e data no nome: quem baixa de várias campanhas não fica com "export (1)". */
  private nomeDoArquivoExportado(): string {
    return `adesoes-campanha-${this.campanhaId()}-${toLocalIsoDate(new Date())}.xlsx`;
  }

  /** Dispara o download de um Blob via link temporário. */
  private baixarArquivo(blob: Blob, nomeArquivo: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = nomeArquivo;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Libera a memória do Blob; o download já foi iniciado pelo clique acima.
    URL.revokeObjectURL(url);
  }

  toggleMenu(id: string, event: Event): void {
    event.stopPropagation();

    const abrindo = this.menuAbertoId() !== id;
    this.menuAbertoId.set(abrindo ? id : null);

    if (!abrindo) {
      this.menuPosition.set(null);
      return;
    }

    this.menuPosition.set(getActionsMenuPosition(event));
    this.adjustMenuPosition();
  }

  private adjustMenuPosition(): void {
    afterNextRender(
      () => {
        const adjusted = adjustActionsMenuPosition(this.menuPosition());
        if (adjusted) {
          this.menuPosition.set(adjusted);
        }
      },
      { injector: this.injector },
    );
  }

  onVisualizarProtocolo(adesao: Adesao, event: Event): void {
    event.stopPropagation();
    this.menuAbertoId.set(null);

    const id = this.campanhaId();
    if (!id) {
      this.protocoloSelecionado.set(adesao);
      return;
    }

    this.campanhaService.listarAdesoes(id).subscribe((lista) => {
      const atual = lista.find((item) => item.id === adesao.id);
      this.protocoloSelecionado.set(atual ?? adesao);
    });
  }

  onFecharProtocolo(): void {
    this.protocoloSelecionado.set(undefined);
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }

  private loadCampanha(): void {
    const id = this.campanhaId();
    if (!id) {
      void this.router.navigate(['/not-found']);
      return;
    }

    this.loaderService.showLoader();

    this.campanhaService
      .obterCampanha(id)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (campanha) => {
          if (!campanha) {
            void this.router.navigate(['/not-found']);
            return;
          }

          this.campanha.set(campanha);
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar a campanha.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
